/* eslint-disable no-useless-catch */
/* eslint-disable react/prop-types */
import { useEffect, useRef, useCallback, useState } from 'react';
import proj4 from 'proj4';
import { useLeafletContext } from '@react-leaflet/core';
import { useMap } from 'react-leaflet';
import parseGeoraster from 'georaster';
import GeoRasterLayer from 'georaster-layer-for-leaflet';
import L from 'leaflet';
import geoblaze from 'geoblaze';
import {
  getColorScale,
  pixelValuesToColorFn,
  popoverFormatter
} from 'helpers/leaflet-utils';

window.proj4 = proj4;

const GeotiffLayer = ({
  url,
  title,
  coordinates,
  layerVisible,
  legendData,
  isParent,
  childFeatures,
  zIndex
}) => {
  const geoTiffLayerRef = useRef();
  const georasterRef = useRef();
  const context = useLeafletContext();
  const map = useMap();
  const [isLoaded, setIsLoaded] = useState(false);

  const isMobileBrowser = /Mobi|Android|iPad|iPhone|iPod/i.test(
    navigator.userAgent
  );

  const handleClick = useCallback(
    e => {
      if (!georasterRef.current) return;

      const clickedControl = e.originalEvent.target.closest('.leaflet-control');
      if (clickedControl) return;

      const latlng = map.mouseEventToLatLng(e.originalEvent);

      if (
        isParent &&
        Array.isArray(childFeatures) &&
        childFeatures.length > 0
      ) {
        const clickedChild = childFeatures.find(childFeature =>
          L.geoJSON(childFeature).getBounds().contains(latlng)
        );
        if (clickedChild) return;
      }

      const pixelValue = geoblaze.identify(georasterRef.current, [
        latlng.lng,
        latlng.lat
      ]);

      if (pixelValue !== null) {
        const pixelColor = pixelValuesToColorFn(
          pixelValue[0],
          title,
          legendData
        );
        if (!pixelColor) return;
        const colorData = legendData || getColorScale(title);
        const legend = colorData.find(
          obj => obj.bg_color?.toLowerCase() === pixelColor.toLowerCase()
        )?.legend;
        if (!legend) return;
        const content = popoverFormatter(
          latlng,
          pixelColor,
          legend,
          pixelValue,
          null
        );
        L.popup().setLatLng(e.latlng).setContent(content).openOn(map);
      }
    },
    [map, title, legendData, isParent, childFeatures]
  );

  useEffect(() => {
    const container = context.layerContainer || context.map;

    const loadGeoTiff = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Response not ok');
        const arrayBuffer = await response.arrayBuffer();
        const georaster = await parseGeoraster(arrayBuffer);
        georasterRef.current = georaster;

        if (geoTiffLayerRef.current) {
          container.removeLayer(geoTiffLayerRef.current);
        }

        geoTiffLayerRef.current = new GeoRasterLayer({
          georaster,
          resolution: 256,
          opacity: isParent ? 0.5 : 0.8,
          name: 'index_layer',
          pixelValuesToColorFn: values =>
            pixelValuesToColorFn(values[0], title, legendData),
          zIndex
        });

        container.addLayer(geoTiffLayerRef.current);
        setIsLoaded(true);
      } catch (error) {
        console.error('Error loading GeoTIFF:', error);
        // map.fitBounds(coordinates);
      }

      if (isMobileBrowser) {
        map.dragging.disable();
        map.tapHold.disable();
      }
    };

    loadGeoTiff();

    return () => {
      if (geoTiffLayerRef.current) {
        container.removeLayer(geoTiffLayerRef.current);
      }
      map.closePopup();
    };
  }, [
    context,
    url,
    map,
    coordinates,
    title,
    legendData,
    isParent,
    zIndex,
    isMobileBrowser
  ]);

  useEffect(() => {
    if (isLoaded) {
      map.on('click', handleClick);
    }
    return () => {
      map.off('click', handleClick);
    };
  }, [map, handleClick, isLoaded]);

  useEffect(() => {
    if (geoTiffLayerRef.current) {
      geoTiffLayerRef.current.setOpacity(
        layerVisible ? (isParent ? 0.5 : 0.8) : 0
      );
    }
  }, [layerVisible, isParent]);

  return null;
};

export default GeotiffLayer;
